/**
 * Media delivery API.
 *
 * Backend: `app/api/media.py`. Phase 1 of
 * `docs/plans/manuscript-file-delivery.md` — one read-only route that answers
 * whether a manuscript scraper account can reach a file's channel at all. It
 * makes no Telegram call and queues no job, so it is cheap to ask and safe to
 * ask often; `/fetch` and `/stream` arrive in later phases.
 */

import axios from 'axios';

import apiClient from './client';

/**
 * Why a file is (or is not) reachable. Mirrors `AvailabilityReason` in
 * `backend/app/schemas/media.py` — a new member there needs a sentence here.
 *
 *  - `available`  a manuscript account is a confirmed member of the channel.
 *  - `unverified` it holds an allowlist row, but no dialogs snapshot has ever
 *                 confirmed the join landed. Still downloadable (the worker
 *                 gates on the row, not on `verified_at`) — just unproven.
 *  - `left`       the account was a member and has since left or been removed.
 *  - `no_account` no manuscript account was ever allowlisted on the channel.
 *  - `no_file` the media row carries no `file_id`, so there is nothing to
 *    fetch at all. A property of the row, not the channel — it outranks every
 *    membership answer, and the backend returns it before consulting them.
 */
export type AvailabilityReason = 'available' | 'unverified' | 'left' | 'no_account' | 'no_file';

export interface MediaAvailability {
  downloadable: boolean;
  reason: AvailabilityReason;
  /** `scraper_accounts.label`, or null when no account can reach the channel. */
  account_label: string | null;
  /** ISO string carrying the server's UTC offset; null when never confirmed. */
  verified_at: string | null;
}

/**
 * `messageId` is `Messages.id` — the internal row PK, NOT the Telegram
 * `message_id`. The backend resolves the channel from it so no Telegram peer
 * id ever reaches a URL or a log.
 *
 * A 404 is a real answer, not only "no such row": messages outside the
 * manuscript channel set are indistinguishable from missing ones on purpose
 * (the scope clamp, plan §5.1), so callers must render it as "out of scope"
 * rather than as an error.
 */
export const getMediaAvailability = async (messageId: number): Promise<MediaAvailability> => {
  const response = await apiClient.get<MediaAvailability>(`/media/${messageId}/availability`);
  return response.data;
};

/**
 * True when an availability call failed because the message is outside the
 * manuscript channel set (the scope clamp, §5.1). It is a 404 and not a 403 on
 * purpose — a 403 would confirm the row exists and turn the sequential
 * `Messages.id` space into an oracle for which channels the operator scrapes.
 * Callers render it as "out of scope", never as a broken request.
 */
export const isOutOfScope = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

// ===========================================================================
// Phase 2/3 delivery — `/stream` and `/fetch`
//
// The backend contract lives in `manuscript/backend/app/api/media.py` and
// `app/schemas/media.py`; every literal below is copied from there and a change
// on either side is a change on both.
// ===========================================================================

/**
 * The API base as a URL a media ELEMENT can use.
 *
 * `apiClient` has this in `defaults.baseURL`, but an `<img src>` cannot go
 * through axios — this is the one place the base is needed as a plain string.
 * It stays relative in production (`.env.production`), which is what keeps the
 * request same-origin and therefore visible to the service worker: a worker
 * cannot intercept another origin, so an absolute base here would silently
 * disable the whole Phase 4 auth path.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * The URL an `<img>`, an `<iframe>` or an `<a download>` points at.
 *
 * NO TOKEN, EVER (§6.3) — the service worker attaches the bearer as a header
 * on a fetch of its own. `dl=1` is the ONLY query parameter this route takes:
 * it flips `Content-Disposition` to `attachment` and is the single condition
 * under which the backend writes a `media_download` audit row (and only for a
 * whole-body response — range requests and previews are deliberately not
 * audited, or one PDF read would write hundreds of rows).
 */
export const mediaStreamUrl = (messageId: number, download = false): string =>
  `${API_BASE}/media/${messageId}/stream${download ? '?dl=1' : ''}`;

/** Mirrors `MediaFetchState` in `backend/app/schemas/media.py`. Four, and only four. */
export type MediaFetchState = 'queued' | 'running' | 'ready' | 'unavailable';

/**
 * Mirrors `MediaFetchReason`. A CLOSED set: the raw `scraper_jobs.error` string
 * is never put on the wire (§2.5), so every failure arrives as one of these and
 * anything the backend cannot classify arrives as `unknown`.
 *
 * `not_requested` is not a failure — it is the honest answer to "is it here?"
 * when nobody has ever asked, and it is the SPA's cue to POST.
 */
export type MediaFetchReason =
  | 'not_requested' | 'canceled' | 'expired' | 'unreachable'
  | 'flood_wait' | 'cache_full' | 'too_large' | 'gone' | 'unknown';

export interface MediaFetchResponse {
  state: MediaFetchState;
  job_id: number | null;
  /** True when this request attached to a job another reviewer already created. */
  joined: boolean;
}

export interface MediaFetchStatus {
  state: MediaFetchState;
  /**
   * ⚠ A CHECKPOINT SNAPSHOT, NOT A LIVE COUNTER — measured 2026-08-22: it
   * advances in exact 8 MiB steps (`_CHECKPOINT_BYTES`) and the partial file on
   * disk runs several MB AHEAD of it. Render it as a lower bound; never as
   * "bytes received so far".
   */
  loaded: number | null;
  /** From Telethon, so it can be present while `Media.file_size` is NULL. */
  total: number | null;
  reason: MediaFetchReason | null;
  job_id: number | null;
}

/**
 * Everything a non-2xx `/fetch` answer carries, flattened.
 *
 * The backend builds these as raw `JSONResponse`s rather than `HTTPException`s
 * precisely so they can carry `Retry-After` and a machine-readable
 * `state`/`reason` beside the human `detail` — so reading only `detail` here
 * would throw away the half the UI needs.
 */
export interface MediaFetchError {
  status: number;
  /** `rate_limited`, `unavailable`, `disabled`, … — the machine field. */
  state: string | null;
  reason: string | null;
  detail: string | null;
  /** Seconds, from the `Retry-After` header. Null when the header is absent. */
  retryAfter: number | null;
  /** Present on 429: the limit that was hit and how much of it is spent. */
  limit: number | null;
  used: number | null;
  /** Present on the DAILY 429 only — the concurrent one has no fixed reset. */
  resetAt: string | null;
  /** Present on 413. */
  fileSize: number | null;
  maxBytes: number | null;
}

export const parseMediaFetchError = (error: unknown): MediaFetchError | null => {
  if (!axios.isAxiosError(error) || !error.response) return null;
  const { status, data, headers } = error.response;
  const body = (data ?? {}) as Record<string, unknown>;
  const retryRaw = headers?.['retry-after'];
  const retryAfter = retryRaw != null && retryRaw !== '' ? Number(retryRaw) : NaN;
  const num = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;
  return {
    status,
    state: typeof body.state === 'string' ? body.state : null,
    reason: typeof body.reason === 'string' ? body.reason : null,
    detail: typeof body.detail === 'string' ? body.detail : null,
    retryAfter: Number.isFinite(retryAfter) ? retryAfter : null,
    limit: num(body.limit),
    used: num(body.used),
    resetAt: typeof body.reset_at === 'string' ? body.reset_at : null,
    fileSize: num(body.file_size),
    maxBytes: num(body.max_bytes),
  };
};

/**
 * Ask for a file. 200 = already on disk and NOTHING was enqueued; 202 = a job
 * row exists, either created by this call or JOINED (`joined: true`, which is a
 * success — two reviewers opening the same manuscript must produce one job, one
 * claim, one transfer on an aged account).
 *
 * `cached` is derived from the status code because the two 2xx answers mean
 * materially different things to the caller: a cache hit spends nothing, counts
 * against no limit, and releases its queue slot immediately.
 */
export const requestMediaFetch = async (
  messageId: number
): Promise<MediaFetchResponse & { cached: boolean }> => {
  const response = await apiClient.post<MediaFetchResponse>(`/media/${messageId}/fetch`);
  return { ...response.data, cached: response.status === 200 };
};

/**
 * Poll one fetch — AND heartbeat it.
 *
 * ⚠ This call IS the abandonment heartbeat: it writes `last_polled_at`, and the
 * worker abandons any job that has not been polled for `download_abandon_sec`
 * (180 s live). So polling costs nothing but NOT polling is a decision: a page
 * that stops asking is telling the worker to stop transferring, which is
 * exactly what should happen when the reviewer closes the tab.
 */
export const getMediaFetchStatus = async (messageId: number): Promise<MediaFetchStatus> => {
  const response = await apiClient.get<MediaFetchStatus>(`/media/${messageId}/fetch`);
  return response.data;
};

/**
 * Cancel this file's in-flight fetch. Always 204 past the scope clamp — a
 * DELETE for a job the caller neither created nor may administer is a silent
 * no-op, because saying "that is not yours" would confirm the job exists.
 */
export const cancelMediaFetch = async (messageId: number): Promise<void> => {
  await apiClient.delete(`/media/${messageId}/fetch`);
};
